<script setup lang="ts">
import { ref, watch } from 'vue'
import type { PushApp, PushSetting } from './types'
import { pushAppOptions } from './types'
import { formatTime, statusText } from './usePushCenterState'
import {
  AdminDataTable,
  AdminFilterBar,
  AdminStatusTag,
  RrInput,
  RrSelect,
  type TableColumnType
} from '@xjlv2/admin-layout'

const props = withDefaults(
  defineProps<{
    settings?: PushSetting[]
    apps?: PushApp[]
    appCode?: string
    total?: number
    pageNo?: number
    pageSize?: number
  }>(),
  {
    settings: () => [],
    apps: () => [],
    appCode: '',
    total: 0,
    pageNo: 1,
    pageSize: 10
  }
)

const emit = defineEmits<{
  'update:appCode': [code: string]
  search: [userId: string]
  'page-change': [pageNo: number, pageSize: number]
}>()

const localAppCode = ref(props.appCode)
watch(
  () => props.appCode,
  (code) => {
    localAppCode.value = code
  }
)

function onAppChange(code: string) {
  emit('update:appCode', code)
}

const userIdInput = ref('')

const columns: TableColumnType<PushSetting>[] = [
  { key: 'userId', title: '用户 ID', width: 110 },
  { key: 'appCode', title: '所属应用', width: 120 },
  { key: 'optIn', title: '接收推送', width: 110 },
  { key: 'quiet', title: '免打扰时间段', width: 160 },
  { key: 'channelSettings', title: '通道偏好配置', minWidth: 220 },
  { key: 'updatedAt', title: '更新时间', width: 170 }
]

function handleSearch() {
  emit('search', userIdInput.value)
}

function handleReset() {
  userIdInput.value = ''
  localAppCode.value = ''
  emit('update:appCode', '')
  emit('search', '')
}
</script>

<template>
  <div class="push-tab-pane">
    <AdminFilterBar
      @submit="handleSearch"
      @reset="handleReset"
    >
      <label>
        所属应用
        <RrSelect v-model:value="localAppCode" :options="pushAppOptions(apps)" @change="onAppChange" />
      </label>
      <label>
        用户 ID
        <RrInput
          v-model:value.trim="userIdInput"
          placeholder="输入用户 ID 进行精确筛选"
          @keyup.enter="handleSearch"
        />
      </label>
    </AdminFilterBar>

    <AdminDataTable
      :columns="columns"
      :data-source="settings"
      :current="pageNo"
      :page-size="pageSize"
      :total="total"
      :empty="settings.length === 0"
      empty-title="暂无用户偏好设置"
      empty-description="客户端用户在移动端设置推送开关或免打扰时段后将在此展示。"
      @update:current="(p) => emit('page-change', p, pageSize)"
      @update:page-size="(size) => emit('page-change', 1, size)"
      @refresh="handleSearch"
    >
      <template #bodyCell="{ column, record }">
        <template v-if="column.key === 'userId'">
          <strong>{{ record.userId ?? '-' }}</strong>
        </template>
        <template v-else-if="column.key === 'appCode'">
          <span>{{ record.appCode }}</span>
        </template>
        <template v-else-if="column.key === 'optIn'">
          <AdminStatusTag
            :text="statusText(record.optIn)"
            :tone="record.optIn ? 'success' : 'neutral'"
          />
        </template>
        <template v-else-if="column.key === 'quiet'">
          {{ record.quietStart || '-' }} ~ {{ record.quietEnd || '-' }}
        </template>
        <template v-else-if="column.key === 'channelSettings'">
          <code>{{ JSON.stringify(record.channelSettingsJson) }}</code>
        </template>
        <template v-else-if="column.key === 'updatedAt'">
          {{ formatTime(record.updatedAt) }}
        </template>
      </template>
    </AdminDataTable>
  </div>
</template>

<style scoped>
.push-tab-pane {
  display: flex;
  flex-direction: column;
  gap: var(--rr-space-4);
}
</style>
